import { useContext, useEffect, useMemo } from "react";

import { UnreadsContext } from "@/models/UnreadsContextProvider";
import type { DispatchTableRow } from "./dispatchTableRow";
import { mineIds } from "./useTaskCounts";

/**
 * Points the badge (favicon, title count, desktop count) at the view's rows showing the red dot, and the board's
 * shelf, while the view is mounted, so it matches the table rather than read state; leaving the view hands it back to
 * unread.
 *
 * @param rows - The view's rows after its filters and search, before grouping, so each row counts once.
 * @param shelfRows - The board's task shelf, every row of which waits on someone here; a row also in `rows` counts once.
 */
export function useUnacknowledgedBadge(
  rows: readonly DispatchTableRow[],
  shelfRows: readonly DispatchTableRow[],
): void {
  const { setBadgeIds } = useContext(UnreadsContext);
  // Keyed on the set of ids, not the rows: a driver going from two waiting to one, or the rows reordering, leaves the
  // badge and the app alone.
  const idsKey = mineIds(rows, shelfRows).sort().join(",");
  const ids = useMemo(() => (idsKey ? idsKey.split(",") : []), [idsKey]);

  useEffect(() => {
    setBadgeIds?.(ids);
  }, [setBadgeIds, ids]);

  useEffect(() => () => setBadgeIds?.(null), [setBadgeIds]);
}
